import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SecurityContext,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator, MatPaginatorSelectConfig, PageEvent } from '@angular/material/paginator';
import { getAdjacentPages } from './get-adjacent-pages';
import { DomSanitizer } from '@angular/platform-browser';

export interface TablePaginatorPageChange {
  pageSize: number;
  pageIndex: number;
}

@Component({
  selector: 'miq-table-paginator',
  template: `<mat-paginator
    #paginator
    showFirstLastButtons="true"
    [ngClass]="valueClassPrefix ? valueClassPrefix + '-' + pageSize : ''"
    [length]="length"
    [pageSize]="pageSize"
    [pageIndex]="pageIndex"
    [pageSizeOptions]="pageSizeOptions"
    [selectConfig]="selectConfig"
    (page)="pageEvent = onPaginatorChange($event)"
    aria-label="Select page"
  ></mat-paginator>`,
  styleUrls: ['./table-paginator.component.scss'],
  standalone: false,
})
export class TablePaginatorComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('paginator', { static: true, read: ElementRef }) paginator: ElementRef<HTMLElement>;
  // * For datasource table usage...
  @ViewChild(MatPaginator, { static: true }) dataPaginator: MatPaginator;
  @Input() set pageIndex(value: number) {
    this._pageIndex = value || 0;
  }
  get pageIndex() {
    return this._pageIndex;
  }
  @Input() length: number;
  @Input() isGroupedView = false;
  @Input() pageSizeOptions = [10, 15, 20];
  @Input() pageSize = this.pageSizeOptions[0];
  @Input() useDataSource = false;
  @Input() getPageSizeWhenUpdating = false;
  @Input() selectConfig: MatPaginatorSelectConfig = {};
  /**
   * if a string value is provided, it will be used as a prefix of a class assigned to the mat-paginator element.
   * A usage of this class is to dynamically change the number for a "All" selected option.
   */
  @Input() valueClassPrefix: string;
  // Used only when isGroupedView is true to validate if the next page button should be enabled or not
  // comparing the shown count with the page size
  @Input() countShown: number;
  // Used to reset the pagination when the table is reloaded
  // This is useful when the table is reloaded with a different length or page size
  // and we want to reset the pagination to the first page
  @Input() resetPagination: any;
  @Output() pageChange: EventEmitter<number | TablePaginatorPageChange> = new EventEmitter(null);

  pageEvent: PageEvent;
  private prevBtn: HTMLButtonElement;
  private firstBtn: HTMLButtonElement;
  private nextBtn: HTMLButtonElement;
  private lastBtn: HTMLButtonElement;
  private pagesEl: HTMLDivElement;
  private numOfPages: number;
  private maxPages = 6;
  private pageGroup = 1;
  private _pageIndex = 0;
  /**
   * * An "unlisten" function for disposing of this handler.
   * * https://angular.io/api/core/Renderer2#listen
   */
  private listener: () => void;

  constructor(
    private renderer: Renderer2,
    private sanitizer: DomSanitizer
  ) {}

  private _hasGroupViewConfigsChanged(changes: SimpleChanges): boolean {
    return (
      (changes.isGroupedView && changes.isGroupedView.currentValue && !changes.isGroupedView.firstChange) ||
      (this.isGroupedView && changes.countShown?.currentValue != changes.countShown?.previousValue)
    );
  }

  // eslint-disable-next-line complexity
  ngOnChanges(changes: SimpleChanges) {
    const length = changes.length;
    let pageIndex = changes.pageIndex;
    if (changes.resetPagination && !changes.resetPagination.firstChange) {
      pageIndex = { currentValue: 0, previousValue: this.pageIndex, firstChange: false, isFirstChange: () => false };
      this.pageIndex = 0;
    }
    if (
      this._hasGroupViewConfigsChanged(changes) ||
      (changes.resetPagination && !changes.resetPagination.firstChange)
    ) {
      this.cleanDOM(this.numOfPages);
      this.setUpDOM();
      if (!this.isGroupedView) {
        this.setUpPages();
      }
      this.updatePages();
    } else if (changes.isGroupedView && changes.isGroupedView.previousValue) {
      this.setUpPages();
      this.updatePages();
    }

    // Recalculate the page group in case the page index change to 0 from an index that is not part of the group anymore
    // from pageIndex 12 to index 0
    if (this.numOfPages == null) {
      this.numOfPages = Math.ceil(this.length / this.pageSize);
    }

    if (pageIndex != null) {
      this.#setPageGroup(this.pageIndex);
    }

    if (pageIndex && !length && pageIndex.previousValue !== pageIndex.currentValue && !pageIndex.firstChange) {
      this.updatePages();
    }
    if (length && length.previousValue !== length.currentValue) {
      const prevNumOfPages = Math.ceil(length.previousValue / this.pageSize);
      const currentNumOfPages = Math.ceil(length.currentValue / this.pageSize);
      if (length.previousValue && prevNumOfPages !== currentNumOfPages) {
        this.cleanDOM(currentNumOfPages);
      }
      if (!this.validateDOM()) {
        this.setUpDOM();
      } else {
        if (!this.isGroupedView) {
          this.setUpPages();
        }
        this.updatePages();
      }
    }
  }

  ngAfterViewInit() {
    // * Create/Get necessary DOM elements...
    this.setUpDOM();
    this.renderer.addClass(this.pagesEl, 'pages-container');
    this.prevBtn?.insertAdjacentElement('afterend', this.pagesEl);
    if (this.length) {
      if (!this.isGroupedView) {
        this.setUpPages();
      }
      this.updatePages();
    }
  }

  ngOnDestroy() {
    this.listener();
  }

  onPaginatorChange(event: PageEvent): PageEvent {
    const { pageIndex, pageSize } = event;
    if (this.pageSize !== pageSize) {
      this.pageSize = pageSize;
      const prevNumOfPages = this.numOfPages;
      this.numOfPages = Math.ceil(this.length / this.pageSize);
      this.pageGroup = Math.max(pageIndex % 2 === 0 ? pageIndex - 3 : pageIndex - 2, 1);
      if (this.numOfPages < this.maxPages || prevNumOfPages !== this.numOfPages) {
        this.cleanDOM(this.numOfPages);
        if (!this.isGroupedView) {
          this.setUpPages();
        }
      }
    }
    this.#setPageGroup(pageIndex);

    this.pageIndex = pageIndex;
    this.updatePages(true);
    return event;
  }

  #setPageGroup(pageIndex: number): void {
    if (pageIndex === 0) {
      // * Navigated to first page...
      this.pageGroup = 1;
    } else if (pageIndex === this.numOfPages - 1 && this.numOfPages > this.maxPages) {
      // * Navigated to last page...
      this.pageGroup = this.numOfPages - this.maxPages + 1;
    } else if (this.maxPages + this.pageGroup - pageIndex === 2 && pageIndex < this.numOfPages - 1) {
      // * Navigated to any page from left to right...
      this.pageGroup += 2;
    } else if (pageIndex === this.pageGroup && this.pageGroup !== 1) {
      // * Navigated to any page from right to left...
      this.pageGroup -= 2;
    }
  }

  navigateToPage(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.nodeName === 'SPAN') {
      const pageValue = (event.target as HTMLSpanElement).getAttribute('data-page');
      const prevPage = this.pageIndex;
      if (pageValue === '+2' || pageValue === '-2') {
        this.pageGroup += Number(pageValue);
        this.pageIndex = this.pageGroup + 2;
      } else {
        this.pageIndex = Number(pageValue);
      }
      this.updatePages(true);
      if (this.useDataSource) {
        // * Manually trigger page changes in case a table component is using datasource...
        const page: PageEvent = {
          previousPageIndex: prevPage,
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          length: this.length,
        };
        this.dataPaginator.page.emit(page);
      }
    }
  }

  private setUpDOM(): void {
    this.firstBtn = this.paginator.nativeElement.querySelector('.mat-mdc-paginator-navigation-first');
    this.prevBtn = this.paginator.nativeElement.querySelector('.mat-mdc-paginator-navigation-previous');
    this.nextBtn = this.paginator.nativeElement.querySelector('.mat-mdc-paginator-navigation-next');
    this.lastBtn = this.paginator.nativeElement.querySelector('.mat-mdc-paginator-navigation-last');
    this.pagesEl = this.pagesEl ? this.pagesEl : this.renderer.createElement('DIV');
    // * Event delegation for pages...
    if (!this.listener) {
      this.listener = this.renderer.listen(this.pagesEl, 'click', this.navigateToPage.bind(this));
    }
  }

  private setUpPages(): void {
    // Clean the dom before start adding the number of the pages
    this.pagesEl.innerHTML = '';
    this.numOfPages = Math.ceil(this.length / this.pageSize);
    // * Create pages children...
    const pagesToShow = Math.min(this.numOfPages, this.maxPages);
    for (let page = 0; page < pagesToShow; page++) {
      const pageInstance = this.renderer.createElement('SPAN');
      this.renderer.appendChild(this.pagesEl, pageInstance);
    }
  }

  private updatePages(emitChange = false): void {
    if (!this.isGroupedView) {
      const pages = getAdjacentPages({ pageIndex: this.pageIndex, totalCount: this.length, pageSize: this.pageSize });
      Array.from(this.pagesEl.children).forEach((pageEl, index) => {
        const { label, value, current } = pages[index];
        // Avoid possible XSS attacks by only allowing numbers including +2 and -2 values
        let dataPage = '';
        const innerTextLabel = this.sanitizer.sanitize(SecurityContext.NONE, label);
        if (!isNaN(Number(value))) {
          dataPage = this.sanitizer.sanitize(SecurityContext.NONE, value);
        }
        this.renderer.setProperty(pageEl, 'textContent', innerTextLabel);
        this.renderer.setAttribute(pageEl, 'data-page', dataPage);
        if (current) {
          this.renderer.addClass(pageEl, 'current-page');
        } else {
          this.renderer.removeClass(pageEl, 'current-page');
        }
      });
    }

    this.updateButtonStylesByGroupFlag(emitChange);
  }

  /**
   * If isGroupedView is true, only the firstPageButton and nextPageButton will be displayed.
   * Else, we call the default method.
   */
  private updateButtonStylesByGroupFlag(emitChange: boolean) {
    if (this.isGroupedView) {
      if (this.firstBtn) {
        this.renderer.removeStyle(this.firstBtn, 'display');
      }

      if (this.prevBtn) {
        this.renderer.setStyle(this.prevBtn, 'display', 'none');
      }

      if (this.lastBtn) {
        this.renderer.setStyle(this.lastBtn, 'display', 'none');
      }
      // If there are not going to be more pages, disable the next button
      if (this.countShown < this.pageSize) {
        this.renderer.setAttribute(this.nextBtn, 'disabled', 'true');
        this.renderer.setStyle(this.nextBtn, 'color', 'rgba(0, 0, 0, 0.26)');
      } else {
        // In case it is not visible, make it visible
        this.renderer.removeStyle(this.nextBtn, 'display');
        this.renderer.removeAttribute(this.nextBtn, 'disabled');
        this.renderer.removeStyle(this.nextBtn, 'color');
      }

      // If the pageIndex is 0, disable the first button
      // This is to avoid the first button to be clickable when the pageIndex is 0
      if (this.pageIndex === 0 && this.firstBtn) {
        this.renderer.setAttribute(this.firstBtn, 'disabled', 'true');
        this.renderer.setStyle(this.firstBtn, 'color', 'rgba(0, 0, 0, 0.26)');
      } else if (this.firstBtn) {
        this.renderer.removeAttribute(this.firstBtn, 'disabled');
        this.renderer.removeStyle(this.firstBtn, 'color');
      }

      if (emitChange) {
        this.emitChanges();
      }
    } else {
      this.updateButtonStyles(emitChange);
    }
  }

  private updateButtonStyles(emitChange: boolean) {
    if (this.pageIndex === 0) {
      this.renderer.setStyle(this.firstBtn, 'display', 'none');
      this.renderer.setStyle(this.prevBtn, 'display', 'none');
    } else {
      this.renderer.removeStyle(this.firstBtn, 'display');
      this.renderer.removeStyle(this.prevBtn, 'display');
    }

    if (this.pageIndex === this.numOfPages - 1) {
      this.renderer.setStyle(this.lastBtn, 'display', 'none');
      this.renderer.setStyle(this.nextBtn, 'display', 'none');
    } else {
      this.renderer.removeStyle(this.lastBtn, 'display');
      this.renderer.removeStyle(this.nextBtn, 'display');
    }
    if (emitChange) {
      this.emitChanges();
    }
  }

  private emitChanges(): void {
    this.pageChange.emit(
      this.getPageSizeWhenUpdating ? { pageSize: this.pageSize, pageIndex: this.pageIndex } : this.pageIndex + 1
    );
  }

  private cleanDOM(numOfPages: number): void {
    // * Clean DOM Elements if length is less than max pages limit...
    if (this.pagesEl) {
      this.pagesEl.innerHTML = '';
    }
    if (this.pageIndex > numOfPages - 1) {
      this.pageIndex = 0;
      this.pageGroup = 1;
    }
  }

  private validateDOM(): boolean {
    return !!(this.pagesEl && this.firstBtn && this.nextBtn && this.prevBtn && this.lastBtn);
  }
}
